import axios from 'axios';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/config';

const Login = () => {
  const [formData, setFormData] = useState({
    login: '', // can be email or phone
    password: '',
    remember_me: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/';

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Function to detect if input is email or phone
  const detectInputType = (value) => {
    // Check if it's an email (contains @)
    if (value.includes('@')) {
      return 'email';
    }
    // Check if it's a phone number (contains only digits, spaces, dashes, plus)
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (phoneRegex.test(value) && value.replace(/[\s\-\+\(\)]/g, '').length >= 7) {
      return 'tel';
    }
    return 'text';
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.login) {
      newErrors.login = 'Phone number or email is required';
    } else {
      const inputType = detectInputType(formData.login);
      
      if (inputType === 'email') {
        // Validate email format
        if (!/\S+@\S+\.\S+/.test(formData.login)) {
          newErrors.login = 'Please enter a valid email address';
        }
      } else if (inputType === 'tel') {
        // Validate phone number (basic validation)
        const cleanPhone = formData.login.replace(/[\s\-\+\(\)]/g, '');
        if (cleanPhone.length < 7) {
          newErrors.login = 'Please enter a valid phone number';
        }
      } else {
        // If it's neither clearly email nor phone, show generic error
        newErrors.login = 'Please enter a valid email address or phone number';
      }
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getInputType = () => {
    return detectInputType(formData.login);
  };

  const getInputLabel = () => {
    const inputType = detectInputType(formData.login);
    if (inputType === 'email') {
      return 'Email address';
    } else if (inputType === 'tel') {
      return 'Phone number';
    }
    return 'Email or phone number';
  };

  const getInputPlaceholder = () => {
    const inputType = detectInputType(formData.login);
    if (inputType === 'email') {
      return 'Enter your email address';
    } else if (inputType === 'tel') {
      return 'Enter your phone number';
    }
    return 'Enter your email or phone number';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Use AuthContext login function
      const result = await login(formData.login, formData.password, formData.remember_me);
      
      if (result.success) {
        // Transfer localStorage cart items to backend
        await transferLocalCartToBackend();
        
        // Redirect based on user role
        if (result.user && result.user.role === 'admin') {
          navigate('/admin/dashboard', { replace: true });
        } else {
          // Check if we're on a store page and redirect to account
          const currentPath = window.location.pathname;
          const pathParts = currentPath.split('/');
          if (pathParts.length >= 3 && pathParts[1] === 'stores') {
            const storeSlug = pathParts[2];
            navigate(`/stores/${storeSlug}/account`, { replace: true });
          } else {
            const redirectPath = result.redirectTo || from;
            navigate(redirectPath, { replace: true });
          }
        }
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: 'Login failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const transferLocalCartToBackend = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Get all localStorage keys that start with 'cart_'
      const cartKeys = Object.keys(localStorage).filter(key => key.startsWith('cart_'));
      
      for (const cartKey of cartKeys) {
        const storeId = cartKey.replace('cart_', '');
        const localCartItems = JSON.parse(localStorage.getItem(cartKey) || '[]');
        
        if (localCartItems.length > 0) {
          // Add each item to the backend cart
          for (const item of localCartItems) {
            try {
              await axios.post(getApiUrl(`/cart/${storeId}/add`), {
                product_id: item.id,
                quantity: item.quantity
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } catch (error) {
              console.error(`Error adding item ${item.id} to backend cart:`, error);
            }
          }
          
          // Clear the localStorage cart for this store
          localStorage.removeItem(cartKey);
        }
      }
    } catch (error) {
      console.error('Error transferring cart items:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center">
          <Link to="/" className="mr-4">
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-xl font-semibold text-gray-900">Sign In</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome back
            </h2>
            <p className="text-gray-600">
              Sign in to your account to continue
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-2xl p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {errors.general}
                </div>
              )}

              {/* Login Input */}
              <div>
                <label htmlFor="login" className="block text-sm font-medium text-gray-700 mb-2">
                  {getInputLabel()}
                </label>
                <input
                  type={getInputType()}
                  id="login"
                  name="login"
                  value={formData.login}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                    errors.login ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={getInputPlaceholder()}
                  disabled={loading}
                />
                {errors.login && (
                  <p className="mt-1 text-sm text-red-600">{errors.login}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your password"
                  disabled={loading}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>



              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 text-white py-3 px-4 rounded-xl hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            {/* Register Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 